import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Brain, FileQuestion, Tag, History } from "lucide-react";
import { PageHeader, Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import AIQuestionsList from "./AIQuestionsList";
import CategoryManager from "./CategoryManager";
import AdminAIAttemptHistory from "./AdminAIAttemptHistory";
import { AIBankHeaderContext } from "./aiBankHeaderContext";

const VALID_TABS = ["questions", "categories", "attempts"];

const TAB_META = {
  questions:  { icon: FileQuestion, label: "Questions" },
  categories: { icon: Tag,           label: "Categories" },
  attempts:   { icon: History,       label: "Attempts" },
};

/**
 * AIBankPage — single hub that consolidates AI Questions, Categories, and
 * Attempts into one tabbed page so the sidebar isn't cluttered.
 *
 * URL is :tab-aware so each tab is bookmarkable:
 *   /ai-bank              → questions
 *   /ai-bank/categories   → categories
 *   /ai-bank/attempts     → attempts
 */
const AIBankPage = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const activeTab = VALID_TABS.includes(tab) ? tab : "questions";

  const [headerActions, setHeaderActions] = useState(null);
  const ctxValue = useMemo(() => ({ setHeaderActions }), []);

  const handleChange = (next) => {
    setHeaderActions(null);
    navigate(next === "questions" ? "/ai-bank" : `/ai-bank/${next}`, { replace: false });
  };

  return (
    // No stylesheet — converted in Phase 5. Its three descendant rules reached into
    // child pages (.ai-page, .ai-cats-page) and into the Tabs primitive to trim
    // spacing; they are arbitrary variants now, which keeps the reach explicit and
    // local instead of hiding it in a sheet the child pages know nothing about.
    <div className="tw-flex tw-flex-col tw-gap-4 [&_.ai-cats-page]:tw-gap-4 [&_.ai-page]:tw-gap-4 [&_.custom-tabs-trigger]:tw-inline-flex [&_.custom-tabs-trigger]:tw-items-center [&_.custom-tabs-trigger]:tw-gap-1.5">
      <PageHeader
        icon={Brain}
        title="AI Question Bank"
        description="Manage AI interview questions, categories, and student attempts in one place."
        actions={headerActions}
      />

      <AIBankHeaderContext.Provider value={ctxValue}>
        <Tabs value={activeTab} onChange={handleChange} className="tw-flex tw-flex-col tw-gap-4">
        <TabsList>
          {VALID_TABS.map((key) => {
            const Icon = TAB_META[key].icon;
            return (
              <TabsTrigger key={key} value={key}>
                <Icon size={14} />
                <span>{TAB_META[key].label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="questions">
          <AIQuestionsList embedded />
        </TabsContent>
        <TabsContent value="categories">
          <CategoryManager
            type="learn_tech"
            title="Tech Stacks"
            description="Categories for organising AI questions and the Learn with AI picker. Manage Mock Interview options and more on the AI Categories page."
            addPlaceholder="e.g. GraphQL, Rust, System Design"
            showCounts
          />
        </TabsContent>
        <TabsContent value="attempts">
          <AdminAIAttemptHistory embedded />
        </TabsContent>
      </Tabs>
      </AIBankHeaderContext.Provider>
    </div>
  );
};

export default AIBankPage;
